import SocialToggle from "features/social-toggles/blocks/SocialToggle";
import { useFlag } from "hooks/useFlags";
import { useUser } from "hooks/useUser";
import React, { useEffect, useState } from "react";

type SocialButtonsProps = {
  meta: any;
  cardContentType: string | undefined;
  cardContentId: string;
  isReviewing: boolean;
  isCollectiveContent: boolean;
  isMyStuff: boolean;
  reviewCount: number;
  reviewedByUser: boolean;
  onReviewToggle?: () => void;
};

const SocialButtons: React.FC<SocialButtonsProps> = ({
  meta,
  cardContentType,
  cardContentId,
  isReviewing,
  isCollectiveContent,
  isMyStuff,
  reviewCount,
  reviewedByUser,
  onReviewToggle,
}) => {
  const likesEnabled = useFlag("likesTest");
  const editorialEnabled = useFlag("editorialTest");
  const { isContentEditor } = useUser();

  const favoriteCount = meta?.favoriteCount || 0;
  const favoritedByUser = meta?.favoritedByUser || false;

  const [likeState, setLikeState] = useState({
    count: meta?.likeCount || 0,
    toggled: meta?.likedByUser || false,
  });
  const [dislikeState, setDislikeState] = useState({
    count: meta?.dislikeCount || 0,
    toggled: meta?.dislikedByUser || false,
  });

  useEffect(() => {
    setLikeState({
      count: meta?.likeCount || 0,
      toggled: meta?.likedByUser || false,
    });
    setDislikeState({
      count: meta?.dislikeCount || 0,
      toggled: meta?.dislikedByUser || false,
    });
  }, [
    meta?.likeCount,
    meta?.likedByUser,
    meta?.dislikeCount,
    meta?.dislikedByUser,
  ]);

  return (
    <>
      {likesEnabled && !isReviewing && likeState.count !== undefined && (
        <SocialToggle
          count={likeState.count}
          toggledByUser={likeState.toggled}
          objectType={cardContentType}
          objectId={cardContentId}
          disabled={isMyStuff}
          onToggle={(data) =>
            setLikeState({
              toggled: (data?.likedByUser as boolean) ?? false,
              count: (data?.likeCount as number) ?? 0,
            })
          }
          onOppositeToggle={(toggled, count) =>
            setDislikeState({ toggled, count })
          }
        />
      )}
      {likesEnabled && !isReviewing && dislikeState.count !== undefined && (
        <SocialToggle
          count={dislikeState.count}
          toggledByUser={dislikeState.toggled}
          objectType={cardContentType}
          objectId={cardContentId}
          kind="dislike"
          disabled={isMyStuff}
          onToggle={(data) =>
            setDislikeState({
              toggled: (data?.dislikedByUser as boolean) ?? false,
              count: (data?.dislikeCount as number) ?? 0,
            })
          }
          onOppositeToggle={(toggled, count) =>
            setLikeState({ toggled, count })
          }
        />
      )}
      {likesEnabled && !isReviewing && favoriteCount !== undefined && (
        <SocialToggle
          count={favoriteCount}
          toggledByUser={favoritedByUser}
          objectType={cardContentType}
          objectId={cardContentId}
          kind="favorite"
          disabled={isMyStuff}
        />
      )}
      {editorialEnabled &&
        isContentEditor &&
        isCollectiveContent &&
        reviewCount !== undefined && (
          <SocialToggle
            count={reviewCount}
            toggledByUser={reviewedByUser}
            objectType={cardContentType}
            objectId={cardContentId}
            kind="review"
            onToggle={onReviewToggle}
          />
        )}
    </>
  );
};

export default SocialButtons;
